import { parse } from "query-string";

const STORAGE_KEY = "oauth_redirect_params";
const DOORKEEPER_STORAGE_KEY = "oauth_doorkeeper_params";

// Doorkeeper authorize endpoint params we want to preserve across a social
// sign-in round-trip. This list mirrors the params Doorkeeper itself accepts.
const DOORKEEPER_PARAM_KEYS = [
  "client_id",
  "redirect_uri",
  "response_type",
  "state",
  "scope",
  "code_challenge",
  "code_challenge_method",
  "approval_prompt",
  "nonce",
];

export const saveRedirectParams = (params) => {
  const { redirect_app, redirect_path, ...otherParams } = params;

  if (redirect_app) {
    const paramsToSave = { redirect_app };

    if (redirect_path) {
      paramsToSave.redirect_path = redirect_path;
    }

    if (Object.keys(otherParams).length > 0) {
      paramsToSave.otherParams = otherParams;
    }

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(paramsToSave),
    );
  }
};

export const getAndClearRedirectParams = () => {
  const stored = localStorage.getItem(STORAGE_KEY);

  if (stored) {
    localStorage.removeItem(STORAGE_KEY);
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error("Failed to parse stored redirect params:", e);
      return null;
    }
  }

  return null;
};

export const clearRedirectParams = () => {
  localStorage.removeItem(STORAGE_KEY);
};

export const useOAuthRedirectSave = () => {
  const saveRedirectFromUrl = () => {
    const queries = parse(window.location.search);
    const { redirect_app } = queries;

    if (redirect_app) {
      saveRedirectParams(queries);
    } else {
      clearRedirectParams();
    }
  };

  return { saveRedirectFromUrl };
};

// --- Doorkeeper (ELMO etc.) helpers ----------------------------------------
//
// When a user clicks "Sign in with Google/Microsoft" while on the Doorkeeper
// authorize endpoint, the social sign-in round-trip may leave the original
// URL behind. We stash the Doorkeeper params so the post-SSO routing can
// re-launch the Doorkeeper handshake instead of dropping the user on the
// Breathe dashboard.

const pickDoorkeeperParams = (params) => {
  const picked = {};
  DOORKEEPER_PARAM_KEYS.forEach((key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
      picked[key] = params[key];
    }
  });
  return picked;
};

const hasMinimumDoorkeeperParams = (params) => {
  return Boolean(params.client_id && params.response_type);
};

export const saveDoorkeeperParams = (params) => {
  const picked = pickDoorkeeperParams(params);

  if (hasMinimumDoorkeeperParams(picked)) {
    localStorage.setItem(DOORKEEPER_STORAGE_KEY, JSON.stringify(picked));
  }
};

export const getAndClearDoorkeeperParams = () => {
  const stored = localStorage.getItem(DOORKEEPER_STORAGE_KEY);

  if (stored) {
    localStorage.removeItem(DOORKEEPER_STORAGE_KEY);
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error("Failed to parse stored Doorkeeper params:", e);
      return null;
    }
  }

  return null;
};

export const clearDoorkeeperParams = () => {
  localStorage.removeItem(DOORKEEPER_STORAGE_KEY);
};

export const saveDoorkeeperParamsFromUrl = () => {
  const queries = parse(window.location.search);

  if (hasMinimumDoorkeeperParams(queries)) {
    saveDoorkeeperParams(queries);
    return true;
  }

  clearDoorkeeperParams();
  return false;
};

export const buildDoorkeeperSearch = (params) => {
  const picked = pickDoorkeeperParams(params);
  const keys = Object.keys(picked);

  if (keys.length === 0) {
    return "";
  }

  const pairs = keys.map(
    (key) => `${encodeURIComponent(key)}=${encodeURIComponent(picked[key])}`,
  );
  return `?${pairs.join("&")}`;
};
